/**
 * The report app's OPTIONAL account — one module-scoped store, so the
 * dock, the wizard and the account sheet all read the same answer.
 *
 * Nothing in the app waits on this. Reporting, favorites and events
 * all work signed out, exactly as they did before accounts existed;
 * the account only adds a name on reports and a list of them.
 *
 * The session cookie IS the account (the endpoints are ordinary web
 * routes on this origin), so every call is `credentials: 'same-origin'`
 * and carries the XSRF-TOKEN cookie back as a header. The cookie
 * rather than the page's csrf meta tag, because /report is served
 * cache-first by the service worker: the meta in a cached shell is the
 * token of whatever session wrote that shell, and signing in rotates
 * it.
 *
 * The signed-in user is mirrored to localStorage so a launch with no
 * signal still knows whose phone this is — the wizard can queue a
 * report "under your name" offline, and the cookie goes with it when
 * the queue flushes.
 */

import { ref } from 'vue';
import { reporterToken, resetReporterToken } from './reporterToken.js';

const CACHE_KEY = 'tiq_report_account';
const BASE = '/api/v1/report-app/account';
// Long enough for a sign-in on one bar, short enough that a dead link
// reads as offline while the reporter is still looking at the sheet.
export const CALL_TIMEOUT_MS = 15_000;

function readCache() {
    try {
        const v = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        return v && typeof v.email === 'string' ? v : null;
    } catch { return null; }
}

function writeCache(user) {
    try {
        if (user) localStorage.setItem(CACHE_KEY, JSON.stringify(user));
        else localStorage.removeItem(CACHE_KEY);
    } catch { /* private mode */ }
}

export const accountUser = ref(readCache());
export const accountReports = ref([]);
export const accountReportsLoaded = ref(false);
export const accountWorkdays = ref([]);
export const accountWorkdaysLoaded = ref(false);

function xsrfToken() {
    const m = typeof document !== 'undefined'
        ? document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
        : null;
    return m ? decodeURIComponent(m[1]) : '';
}

/**
 * One request. Resolves with the parsed body; rejects with an Error
 * carrying the server's own sentence and `code`, because every failure
 * here is shown to somebody holding a phone.
 */
async function call(method, path = '', body = null) {
    const headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    };
    const token = xsrfToken();
    if (token) headers['X-XSRF-TOKEN'] = token;
    if (body) headers['Content-Type'] = 'application/json';

    const offline = () => {
        const err = new Error("You're offline. Try again when you have signal.");
        err.code = 'offline';
        return err;
    };

    // A timeout, so a trailhead link that accepts the request and never
    // answers ends in the offline branch every caller already handles
    // rather than in a spinner nobody can leave.
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), CALL_TIMEOUT_MS) : null;
    let res;
    try {
        res = await fetch(BASE + path, {
            method,
            credentials: 'same-origin',
            headers,
            body: body ? JSON.stringify(body) : undefined,
            signal: ctrl?.signal,
        });
    } catch {
        throw offline();
    } finally {
        if (timer) clearTimeout(timer);
    }
    // NOT OUR ANSWER IS NO ANSWER. Hotel and trailhead Wi-Fi reply to
    // every request with their own sign-in page — a 200, text/html,
    // often after a redirect. Parsed as ours it was `{}`: `data.user`
    // undefined signed the phone out, a check-in "succeeded" with the
    // message `undefined`, and My reports read empty. Every endpoint
    // here answers JSON from this origin, so anything else is the
    // network talking, which is what `offline` already means.
    const contentType = String(res.headers?.get?.('content-type') || '').toLowerCase();
    if (res.redirected === true || contentType.includes('text/html')) {
        throw offline();
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const firstError = data?.errors ? Object.values(data.errors)[0] : null;
        const err = new Error(
            data?.message && res.status !== 422
                ? data.message
                : (Array.isArray(firstError) ? firstError[0] : (data?.message || `Something went wrong (${res.status}).`)),
        );
        err.code = data?.code || (res.status === 419 ? 'expired' : String(res.status));
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}

// Bumped by every answer that CHANGES who is signed in (a verify, a
// sign-out, a delete). A refresh started before one of those carries
// the session as it was when it was sent, and landing afterwards it
// would sign somebody back out, or back in, behind their own tap.
let accountChanges = 0;

function setUser(user, { fromRefresh = false } = {}) {
    if (!fromRefresh) accountChanges += 1;
    accountUser.value = user || null;
    writeCache(accountUser.value);
    if (!user) {
        accountReports.value = [];
        accountReportsLoaded.value = false;
        accountWorkdays.value = [];
        accountWorkdaysLoaded.value = false;
    }
}

/** Ask the server who is signed in. Offline keeps the cached answer. */
export async function refreshAccount() {
    const sentAt = accountChanges;
    try {
        const data = await call('GET');
        if (sentAt === accountChanges) setUser(data.user, { fromRefresh: true });
    } catch (e) {
        if (e.code !== 'offline' && sentAt === accountChanges) setUser(null, { fromRefresh: true });
    }
    return accountUser.value;
}

export function requestSignInCode(email) {
    return call('POST', '/code', { email });
}

/**
 * Spend the code. Rejects with code `needs_name` for an email with no
 * account yet — ask for a name and call again with the SAME code.
 */
export async function verifySignInCode(email, code, name = null, { follow = null } = {}) {
    const data = await call('POST', '/verify', {
        email,
        code,
        name: name || null,
        reporter_token: await reporterToken().catch(() => null),
        // An organization to follow in the same request that confirms
        // the email — the hub's Follow. Never a second call afterwards:
        // the follow must not depend on the tab staying open.
        follow: follow || null,
    });
    setUser(data.user);
    if (Array.isArray(data.follows)) accountFollows.value = data.follows;
    return data;
}

// ── Follows kept on the ACCOUNT ─────────────────────────────────────
// A follow in the app is a push subscription on the PHONE; a browser
// has no phone row, so the hub's Follow is kept against the account
// and the app turns it into a phone follow (applyAccountFollows).

export const accountFollows = ref([]);

/** `{ user, follows }`; signed out is an answer, not an error. */
export async function loadAccountFollows() {
    const data = await call('GET', '/follows');
    setUser(data.user);
    accountFollows.value = Array.isArray(data.follows) ? data.follows : [];
    return data;
}

export async function followOrg(slug) {
    const data = await call('POST', '/follows', { org_slug: slug });
    accountFollows.value = Array.isArray(data.follows) ? data.follows : accountFollows.value;
    return accountFollows.value;
}

export async function unfollowOrg(slug) {
    const data = await call('DELETE', '/follows/' + encodeURIComponent(slug));
    accountFollows.value = Array.isArray(data.follows) ? data.follows : accountFollows.value;
    return accountFollows.value;
}

/**
 * In the app, signed in: tell the server this phone belongs to the
 * account, so the two follow the same organizations from here on — the
 * phone takes up what the account followed from a browser, and a
 * browser unfollow later reaches the phone. `favs` is
 * useReportAppFavorites(). A phone with no device row yet registers one
 * only when the account has something to follow, because registering
 * is what asks for notification permission. Returns the account's
 * follows, or null when nothing was linked.
 */
let linking = null;
export function linkPhoneToAccount(favs) {
    // One at a time: the launch and the phone's first registration can
    // both ask, and the second would only repeat the first.
    if (!linking) linking = linkOnce(favs).finally(() => { linking = null; });
    return linking;
}

async function linkOnce(favs) {
    if (!accountUser.value) return null;
    let follows;
    try {
        follows = (await loadAccountFollows()).follows || [];
    } catch { return null; }
    if (!accountUser.value) return null;
    let id = favs.deviceId.value;
    if (!id) {
        if (follows.length === 0) return null;
        try {
            id = await favs.ensureDevice();
        } catch (e) {
            console.warn('[tiq-report-account] could not register this phone', e?.message || e);
            return null;
        }
    }
    const data = await call('POST', '/device', { device_id: id });
    accountFollows.value = Array.isArray(data.follows) ? data.follows : accountFollows.value;
    await favs.refresh();
    return accountFollows.value;
}

// The phone's device id, read where the favorites composable keeps it,
// so signing out can tell the server which phone is leaving.
function phoneDeviceId() {
    try { return localStorage.getItem('tiq_report_app_device_id') || null; } catch { return null; }
}

export async function updateAccountName(name) {
    const data = await call('PATCH', '', { name });
    setUser(data.user);
    return data.user;
}

export async function signOut() {
    try {
        await call('POST', '/logout', { device_id: phoneDeviceId() });
    } catch (e) {
        // NO SIGNAL, NO SIGN-OUT. The session cookie IS the account and
        // only the server can end it, so clearing the phone's copy here
        // looked signed out until the next launch's refreshAccount asked
        // the server and got the account straight back. Refused instead,
        // with the reason, and the account left exactly as it was.
        if (e?.code === 'offline') throw e;
        // Any other answer reached the server; whatever it said, this
        // phone stops acting for the account.
        await forgetLocally();
        throw e;
    }
    await forgetLocally();
}

async function forgetLocally() {
    setUser(null);
    // What this phone sends from now on must not be claimable by
    // whoever signs in on it next.
    await resetReporterToken().catch(() => {});
}

export async function loadMyReports() {
    const data = await call('GET', '/reports');
    accountReports.value = Array.isArray(data.reports) ? data.reports : [];
    accountReportsLoaded.value = true;
    return accountReports.value;
}

export function requestDeleteCode() {
    return call('POST', '/delete-code');
}

export async function deleteAccount(code) {
    await call('POST', '/delete', { code });
    setUser(null);
    await resetReporterToken().catch(() => {});
}


/**
 * Check in at a workday. `scanned` is whatever the QR code said (the
 * check-in link, or a bare code); without it, `where` names the event
 * and carries the phone's location. Rejects with the server's sentence,
 * and with code `offline` when it never reached the server — which is
 * the case the check-in queue (checkInQueue.js) catches.
 */
export async function checkIn({ scanned = null, where = null, scannedAt = null, eventId = null } = {}) {
    const body = {
        reporter_token: await reporterToken().catch(() => null),
    };
    if (scanned) {
        body.token = String(scanned);
        // A check-in queued with no signal says when it was scanned,
        // and the server checks the window against THAT time.
        if (scannedAt) body.scanned_at = String(scannedAt);
        // The organization's main code, after picking one of its open
        // events.
        if (eventId) body.event_id = Number(eventId);
    } else if (where) {
        Object.assign(body, {
            org_slug: where.orgSlug,
            event_slug: where.eventSlug,
            lat: where.lat,
            lng: where.lng,
            accuracy: where.accuracy ?? null,
        });
    }
    const data = await call('POST', '/checkin', body);
    // The list is now out of date; the next open of the account sheet
    // reloads it rather than showing the check-in missing.
    accountWorkdaysLoaded.value = false;
    return data;
}

export async function loadWorkdays() {
    const data = await call('GET', '/workdays');
    accountWorkdays.value = Array.isArray(data.workdays) ? data.workdays : [];
    accountWorkdaysLoaded.value = true;
    return accountWorkdays.value;
}

/**
 * Is this text one of our check-in codes? Same shape the server's
 * ReportAppCheckInController::tokenFrom accepts from a link.
 */
export function isCheckInCode(text) {
    return /[?&]checkin=[A-Za-z0-9]{16,40}(?:&|#|$)/.test(String(text || '').trim());
}
